import { useState, useEffect, useCallback } from "react";
import DraggableDualList from "../DraggableDualList/DraggableDualList";
import { useUserStore } from "../../contexts/userStore";
import AccessModuleService from "../../services/AccessModuleService";
import { listRfSlots } from "../../helpers/AccessPointParser";
import { AccessIcons } from "../../utils/accessIcons";

// ─── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Build a unique item id matching the old component format.
 */
function makeItemId(accessPoint) {
   return `${accessPoint.id}${accessPoint.subModuleIndex != null ? `/${accessPoint.subModuleIndex}` : ""}`;
}

/**
 * Convert a flat list of access points into a tree of groups (by condominium),
 * compatible with DraggableDualList's node format.
 *
 * @param {Array} accessPoints  – flat items from listRfSlots()
 * @param {Array} groupAccesses – accessGroup.groupAccesses (or []) for filtering
 * @param {boolean} includeSelected – if true, only include points that belong to the group;
 *                                    if false, only include points that do NOT belong.
 */
function buildTree(accessPoints, groupAccesses, includeSelected) {
   const groupMap = new Map();

   for (const ap of accessPoints) {
      const belongsToGroup = groupAccesses.find(
         (ga) =>
            ga.moduleId === ap.id &&
            (ap.subModuleIndex === undefined ||
               (ap.subModuleIndex !== null &&
                  Number(ga.accessIndex) === ap.subModuleIndex))
      );

      const shouldInclude = includeSelected ? !!belongsToGroup : !belongsToGroup;
      if (!shouldInclude) continue;

      const itemId = makeItemId(ap);
      groupMap.set(itemId, {
         id: itemId,
         type: "item",
         label: ap.name,
         icon: AccessIcons[ap.type] ?? null,
         // Keep original references for onChange reconstruction
         originalId: ap.id,
         subModuleIndex: ap.subModuleIndex,
         relationId: belongsToGroup?.id,
      });
   }

   return Array.from(groupMap.values());
}

/**
 * Flatten a tree's leaf items into the same format expected by onChangeList.
 */
function flattenSelectedItems(rightTree) {
   const selected = [];
   for (const node of rightTree) {
      const items = node.type === "group" ? node.children : [node];
      for (const item of items) {
         if (item.type === "item") {
            selected.push({
               id: item.id,
               relationId: item.relationId,
               originalId: item.originalId,
               subIndex: item.subModuleIndex,
               value: item.id,
               label: item.label,
               icon: item.icon,
            });
         }
      }
   }
   return selected;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function InterlockAccessModulesDDL({ interlock, onChangeList }) {
   const { condominiums, selectedCondominio, selectedLicenceId } = useUserStore();
   const [condominiumAccessPoints, setCondominiumAccessPoints] = useState([]);
   const [initialLeft, setInitialLeft] = useState([]);
   const [initialRight, setInitialRight] = useState([]);
   const [ready, setReady] = useState(false);

   // Fetch all access points for the client's licenses
   useEffect(() => {
      if (!selectedCondominio || !selectedLicenceId) return;
      (async () => {
         const { data: response } = await AccessModuleService.listByCondominiumAndLicence(
            selectedCondominio,
            selectedLicenceId,
         );
         setCondominiumAccessPoints(listRfSlots(response));
      })();
   }, [selectedCondominio, selectedLicenceId]);

   // Once access points are loaded, split them into left / right trees
   useEffect(() => {
      if (!condominiumAccessPoints.length) return;
      const interlockAccessModules = interlock?.interlockAccessModules ?? [];

      const left = buildTree(condominiumAccessPoints, interlockAccessModules, false);
      const right = buildTree(condominiumAccessPoints, interlockAccessModules, true);

      setInitialLeft(left);
      setInitialRight(right);
      setReady(true);

      // Notify parent with the initially selected items
      if (onChangeList) onChangeList(flattenSelectedItems(right));
   }, [condominiumAccessPoints]);

   // Called by DraggableDualList whenever the user moves items
   const handleChange = useCallback(
      (_leftTree, rightTree) => {
         if (onChangeList) onChangeList(flattenSelectedItems(rightTree));
      },
      [onChangeList]
   );

   if (!ready) return null; // or a loading skeleton

   return (
      <DraggableDualList
         labelLeft="Acessos Disponíveis"
         labelRight="Acessos Selecionados"
         initialLeft={initialLeft}
         initialRight={initialRight}
         onChange={handleChange}
      />
   );
}